import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Upload, Clock, CheckCircle, XCircle, Eye,
  ArrowLeft, Loader2, X, Music, Mic, ExternalLink,
  ChevronDown, ChevronUp, FileText
} from 'lucide-react';
import { getImageUrl } from '../../utils/api';
import './Submissions.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getFileUrl = (url) => {
  if (!url) return '#';
  return getImageUrl(url) || '#';
};

const TYPE_OPTIONS = [
  { value: 'track', label: 'Track / Demo', icon: Music },
  { value: 'demo', label: 'Demo (Unreleased)', icon: Mic },
];

const KEY_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const KEY_MODES = ['Major', 'Minor'];

const Submissions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNewForm = location.pathname === '/artist/submissions/new';
  const fileInputRef = useRef(null);

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [notesOpen, setNotesOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    submission_type: 'track',
    files: [],
    bpm: '',
    key_note: '',
    key_mode: '',
    track_genre: '',
    collaborators: '',
    stems_available: false,
    notes_to_amr: '',
    isrc: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const getToken = () => localStorage.getItem('artistToken');
  const getHeaders = () => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    if (!isNewForm) fetchSubmissions();
  }, [isNewForm]);

  const fetchSubmissions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artist/submissions`, {
        headers: getHeaders()
      });
      if (res.ok) setSubmissions(await res.json());
    } catch (e) {
      console.error('Failed to fetch submissions:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newUploaded = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API_URL}/api/artist/upload/demo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          newUploaded.push({
            name: file.name,
            url: data.file_url,
            size: file.size,
            type: file.type
          });
        } else {
          const res2 = await fetch(`${API_URL}/api/artist-portal/demos/upload`, {
            method: 'POST',
            body: formData
          });
          if (res2.ok) {
            const data = await res2.json();
            newUploaded.push({
              name: file.name,
              url: data.file_url,
              size: file.size,
              type: file.type
            });
          } else {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploadedFiles(prev => [...prev, ...newUploaded]);
    if (newUploaded.length > 0) {
      toast.success(`${newUploaded.length} file${newUploaded.length > 1 ? 's' : ''} uploaded`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (uploadedFiles.length === 0) { toast.error('Please upload at least one file'); return; }

    setSubmitting(true);
    try {
      const musicalKey = form.key_note && form.key_mode ? `${form.key_note} ${form.key_mode}` : (form.key_note || '');
      const payload = {
        submission_type: form.submission_type,
        title: form.title.trim(),
        description: form.description.trim() || null,
        files: uploadedFiles.map(f => f.url),
        bpm: form.bpm ? parseInt(form.bpm, 10) : null,
        musical_key: musicalKey || null,
        track_genre: form.track_genre.trim() || null,
        collaborators: form.collaborators.trim() || null,
        stems_available: form.stems_available || null,
        notes_to_amr: form.notes_to_amr.trim() || null,
        isrc: form.isrc.trim() || null
      };

      const res = await fetch(`${API_URL}/api/artist/submissions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success('Submission sent! AMR will review it shortly.');
        navigate('/artist/submissions');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit');
      }
    } catch {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle size={16} />;
      case 'rejected': return <XCircle size={16} />;
      case 'reviewed': case 'under_review': return <Eye size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  }) : '';

  // ── New Submission Form ──
  if (isNewForm) {
    return (
      <div className="submissions-page" data-testid="new-submission-form">
        <header className="page-header">
          <button className="back-btn" onClick={() => navigate('/artist/submissions')} data-testid="back-to-submissions">
            <ArrowLeft size={18} />
            Back
          </button>
          <h1>Submit a Track</h1>
          <p>Upload your track with metadata for the AMR team</p>
        </header>

        <form onSubmit={handleSubmit} className="submission-form">
          {/* Section 1 — Track Info */}
          <div className="form-section">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Midnight Drive (Original Mix)"
                required
                data-testid="submission-title"
              />
            </div>

            <div className="form-group">
              <label>Submission Type</label>
              <div className="type-selector">
                {TYPE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={`type-btn ${form.submission_type === opt.value ? 'active' : ''}`}
                      onClick={() => setForm({ ...form, submission_type: opt.value })}
                      data-testid={`type-${opt.value}`}
                    >
                      <Icon size={18} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Section 2 — Track Details (collapsible, open by default) */}
          <div className="form-section collapsible">
            <button
              type="button"
              className="section-toggle"
              onClick={() => setDetailsOpen(!detailsOpen)}
              data-testid="toggle-details"
            >
              <span>Track Details</span>
              <span className="section-hint">helps AMR prepare your release</span>
              {detailsOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            {detailsOpen && (
              <div className="section-body" data-testid="details-section">
                <div className="form-row-2col">
                  <div className="form-group">
                    <label>BPM</label>
                    <input
                      type="number"
                      value={form.bpm}
                      onChange={e => setForm({ ...form, bpm: e.target.value })}
                      placeholder="e.g. 128"
                      min="1"
                      max="999"
                      data-testid="input-bpm"
                    />
                  </div>
                  <div className="form-group">
                    <label>Musical Key</label>
                    <div className="key-selectors">
                      <select
                        value={form.key_note}
                        onChange={e => setForm({ ...form, key_note: e.target.value })}
                        data-testid="select-key-note"
                      >
                        <option value="">Key</option>
                        {KEY_NOTES.map(k => <option key={k} value={k}>{k}</option>)}
                      </select>
                      <select
                        value={form.key_mode}
                        onChange={e => setForm({ ...form, key_mode: e.target.value })}
                        data-testid="select-key-mode"
                      >
                        <option value="">Mode</option>
                        {KEY_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Genre</label>
                  <input
                    type="text"
                    value={form.track_genre}
                    onChange={e => setForm({ ...form, track_genre: e.target.value })}
                    placeholder="e.g. Progressive House"
                    data-testid="input-genre"
                  />
                </div>

                <div className="form-group">
                  <label>Collaborators</label>
                  <input
                    type="text"
                    value={form.collaborators}
                    onChange={e => setForm({ ...form, collaborators: e.target.value })}
                    placeholder="e.g. Feat. Artist Name, Co-produced by..."
                    data-testid="input-collaborators"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.stems_available}
                      onChange={e => setForm({ ...form, stems_available: e.target.checked })}
                      data-testid="check-stems"
                    />
                    <span>I have stems available if needed</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>ISRC</label>
                  <input
                    type="text"
                    value={form.isrc}
                    onChange={e => setForm({ ...form, isrc: e.target.value })}
                    placeholder="Leave blank if unsure"
                    data-testid="input-isrc"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 3 — Notes to AMR (collapsible, closed by default) */}
          <div className="form-section collapsible">
            <button
              type="button"
              className="section-toggle"
              onClick={() => setNotesOpen(!notesOpen)}
              data-testid="toggle-notes"
            >
              <span>Private Notes to AMR Team</span>
              {notesOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            {notesOpen && (
              <div className="section-body" data-testid="notes-section">
                <div className="form-group">
                  <textarea
                    value={form.notes_to_amr}
                    onChange={e => setForm({ ...form, notes_to_amr: e.target.value })}
                    placeholder="Anything we should know about this track? Release timeline, specific feedback you want, etc."
                    rows={4}
                    data-testid="input-notes"
                  />
                  <span className="help-text">These notes are only visible to AMR staff, not on your public profile.</span>
                </div>
              </div>
            )}
          </div>

          {/* Section 4 — Upload Files */}
          <div className="form-section">
            <div className="form-group">
              <label>Upload Audio Files *</label>
              <div className="upload-area" onClick={() => fileInputRef.current?.click()} data-testid="upload-area">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="audio/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  data-testid="file-upload-input"
                />
                {uploading ? (
                  <div className="upload-placeholder">
                    <Loader2 size={28} className="spinner" />
                    <span>Uploading...</span>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <Upload size={28} />
                    <span>Click to select audio files</span>
                    <span className="upload-hint">MP3, WAV, FLAC, AAC, OGG</span>
                  </div>
                )}
              </div>

              {uploadedFiles.length > 0 && (
                <div className="uploaded-files-list" data-testid="uploaded-files">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="uploaded-file-item">
                      <div className="file-icon"><Music size={16} /></div>
                      <div className="file-details">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{formatFileSize(file.size)}</span>
                      </div>
                      <CheckCircle size={14} className="file-check" />
                      <button type="button" className="remove-file-btn" onClick={() => removeUploadedFile(index)}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Section 5 — Description (optional) */}
          <div className="form-section">
            <div className="form-group">
              <label>Description <span className="optional-tag">(optional)</span></label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Public description of this track (optional)"
                rows={3}
                data-testid="submission-description"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/artist/submissions')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting || uploading} data-testid="submit-btn">
              {submitting ? <Loader2 size={16} className="spinner" /> : <Upload size={16} />}
              Submit Track
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── Submissions List ──
  return (
    <div className="submissions-page" data-testid="submissions-list">
      <header className="page-header">
        <div>
          <h1>Track Submissions</h1>
          <p>Submit and track your uploads</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/artist/submissions/new')} data-testid="new-submission-btn">
          <Upload size={16} />
          Submit a Track
        </button>
      </header>

      {loading ? (
        <div className="loading-state">
          <Loader2 size={32} className="spinner" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="empty-state" data-testid="empty-submissions">
          <Music size={48} />
          <h3>No Submissions Yet</h3>
          <p>Submit your tracks and demos for the AMR team to review.</p>
          <button className="btn btn-primary" onClick={() => navigate('/artist/submissions/new')}>
            <Upload size={16} />
            Submit a Track
          </button>
        </div>
      ) : (
        <div className="submissions-list">
          {submissions.map(sub => (
            <div key={sub.id} className="submission-card" data-testid={`submission-${sub.id}`}>
              <div className="card-type">
                {sub.submission_type === 'demo' ? <Mic size={20} /> : <Music size={20} />}
              </div>
              <div className="card-info">
                <h3>{sub.title}</h3>
                {sub.description && <p className="card-desc">{sub.description}</p>}
                <div className="card-meta">
                  <span className="type-label">{sub.submission_type === 'demo' ? 'Demo' : 'Track'}</span>
                  <span className="dot">&middot;</span>
                  <span>{formatDate(sub.created_at)}</span>
                </div>

                {/* Metadata tags */}
                {(sub.bpm || sub.musical_key || sub.track_genre || sub.stems_available) && (
                  <div className="card-tags" data-testid="submission-tags">
                    {sub.bpm && <span className="meta-tag" data-testid="tag-bpm">{sub.bpm} BPM</span>}
                    {sub.musical_key && <span className="meta-tag" data-testid="tag-key">{sub.musical_key}</span>}
                    {sub.track_genre && <span className="meta-tag" data-testid="tag-genre">{sub.track_genre}</span>}
                    {sub.stems_available && <span className="meta-tag stems" data-testid="tag-stems">Stems available</span>}
                  </div>
                )}

                {sub.files?.length > 0 && (
                  <div className="card-files">
                    {sub.files.map((file, i) => {
                      const filename = file.split('/').pop()?.split('?')[0] || `File ${i + 1}`;
                      const isAudio = filename.match(/\.(mp3|wav|aac|flac|ogg|m4a)$/i);
                      return (
                        <a key={i} href={getFileUrl(file)} target="_blank" rel="noopener noreferrer" className="file-link-btn" data-testid={`file-link-${i}`}>
                          {isAudio ? <Music size={12} /> : <ExternalLink size={12} />}
                          {filename.length > 30 ? filename.substring(0, 27) + '...' : filename}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className={`card-status ${sub.status}`}>
                {getStatusIcon(sub.status)}
                {sub.status?.replace('_', ' ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Submissions;
